import {
  type AskPunttMessage,
  type AskPunttProjectData,
  type AskPunttUserQuestion,
} from "@mg/schemas/src/christo/askPuntt";
import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowsInSimple,
  ArrowsOutSimple,
  CaretRight,
  CircleNotch,
  Stop,
  Upload,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Flex,
  IconButton,
  Text,
  TextArea,
  Tooltip,
} from "@radix-ui/themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { askPunttChatRoute } from "./chatRoute";
import {
  type AskPunttMessageExt,
  connectorsArrayToRecord,
  findContextFileIndex,
  mergeMessageStatus,
} from "./chatUtils";
import { AskPunttDropZone } from "./components/AskPunttDropZone";
import {
  AssetViewerPane,
  type AssetViewerPaneHandle,
} from "./components/AssetViewerPane";
import { type CanvasContent } from "./components/canvas/types";
import { CanvasPane } from "./components/CanvasPane";
import { ChatMessage } from "./components/ChatMessage";
import {
  InputPlusMenu,
  type ConnectorOption,
} from "./components/InputPlusMenu";
import { ProjectLoadingPane } from "./components/ProjectLoadingPane";
import { ProjectReviewStatusBanner } from "./components/ProjectReviewStatusBanner";
import { ProjectStatusPane } from "./components/ProjectStatusPane";
import {
  type QuestionFormHandle,
  QuestionForm,
} from "./components/QuestionForm";
import { ReviewChecklist } from "./components/ReviewChecklist";
import { SuggestedResponses } from "./components/SuggestedResponses";
import { ThinkingState } from "./components/ThinkingState";
import { TicketViewPane } from "./components/TicketViewPane";
import { CONNECTOR_ICONS } from "./constants";
import { useAttachedFiles } from "./hooks/useAttachedFiles";
import { useAutoScroll } from "./hooks/useAutoScroll";
import { useFileDrop } from "./hooks/useFileDrop";
import { useTicketViewData } from "./hooks/useTicketView";
import { askPunttRoute } from "./route";

import { PageHeader } from "../../components/PageHeader";
import { getGuidelines } from "../../services/brand-guidelines";
import { editComment } from "../../services/projects";
import { errorAnalyticsPayload, useAnalytics } from "../../utils/analytics";
import { useAppDispatch, useAppSelector } from "../../utils/hooks";
import {
  askPunttKeys,
  useAbortAskPunttMessage,
  useAskPunttChatHistory,
  useAskPunttStreamingState,
  useSendAskPunttMessage,
} from "../../utils/queries/askPuntt";
import { setDrawerOpen } from "../../utils/slices/ui";

const STREAM_POLL_MS = 500;
const FULL_POLL_MS = 3000;
const POLL_TIMEOUT_MS = 300_000;

const SKILL_LABELS: Record<string, string> = {
  claim_generation: "Claims Verification",
  compliance_review: "Compliance Review",
  artwork_review: "Artwork Review",
  brief_creation: "Brief Writing",
  compare: "Brief Comparison",
  project: "Project",
  puntt_actions: "Puntt Capabilities",
  puntt_value: "Workflow Analysis",
};

export default function AskPunttChat() {
  const { chatId } = askPunttChatRoute.useParams();
  const queryClient = useQueryClient();
  const [waitingForAI, setWaitingForAI] = useState(false);
  const [pendingMessageId, setPendingMessageId] = useState<string | null>(null);
  // Tracks the most recently pending message ID so agentSteps merge still works
  // after pendingMessageId is cleared (prevents the flash-on-completion bug).
  const lastPendingIdRef = useRef<string | null>(null);

  const [reviewPolling, setReviewPolling] = useState(false);

  // Full document poll: 1s until we find the pending message, then slow to 3s
  const {
    data: chatData,
    isLoading,
    dataUpdatedAt,
  } = useAskPunttChatHistory(chatId, {
    refetchInterval: waitingForAI
      ? pendingMessageId
        ? FULL_POLL_MS
        : STREAM_POLL_MS
      : reviewPolling
        ? FULL_POLL_MS
        : false,
  });

  // Lightweight streaming poll: only the pending message's streaming fields
  const { data: streamingState } = useAskPunttStreamingState(
    chatId,
    pendingMessageId,
    {
      enabled: !!pendingMessageId && waitingForAI,
      refetchInterval: STREAM_POLL_MS,
    },
  );
  const sendMessage = useSendAskPunttMessage(chatId);
  const abortMessage = useAbortAskPunttMessage(chatId);
  const analytics = useAnalytics("ask_puntt_chat");
  const dispatch = useAppDispatch();
  const sidebarOpen = useAppSelector((state) => state.ui.drawerOpen.app);

  // --- Instrumentation: time-to-first-response ---
  const messageSentAtRef = useRef<number | null>(null);
  const ttfrFiredRef = useRef(false);

  // --- Instrumentation: session depth ---
  const sessionStartRef = useRef(Date.now());
  const messageCountRef = useRef(0);
  const workflowTypeRef = useRef<string | undefined>(undefined);
  const guidelinesQuery = useQuery({
    queryKey: ["brand-guidelines"],
    queryFn: getGuidelines,
  });
  const availableConnectors: ConnectorOption[] = useMemo(
    () =>
      (guidelinesQuery.data?.askPunttConnectors ?? [])
        .filter((c) => c.enabled && c.connected)
        .map((c) => ({
          id: c.connectorId,
          label: `${c.name} search`,
          icon: CONNECTOR_ICONS[c.connectorId] ?? "🔗",
        })),
    [guidelinesQuery.data?.askPunttConnectors],
  );

  const [inputMessage, setInputMessage] = useState("");
  const [sectionContext, setSectionContext] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [localMessages, setLocalMessages] = useState<AskPunttMessageExt[]>([]);
  const {
    attachedItems,
    addFiles,
    addFile,
    removeFile,
    clearAll,
    readyAttachments,
    hasPending,
    localPreviewUrls,
  } = useAttachedFiles({
    onUploadError: (err) =>
      analytics.capture("ask_puntt_upload_error", {
        chat_id: chatId,
        ...errorAnalyticsPayload(err),
      }),
  });
  const { isDragOver, dropZoneProps, handlePaste } = useFileDrop({
    onDrop: addFiles,
  });
  const [canvasDismissed, setCanvasDismissed] = useState(false);
  const [ticketDismissed, setTicketDismissed] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(
    null,
  );
  const [highlightPing, setHighlightPing] = useState<{
    x: number;
    y: number;
    page?: number;
  } | null>(null);
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const {
    scrollContainerRef,
    sentinelRef: messagesEndRef,
    handleScroll: handleMessagesScroll,
    resetScroll: resetAutoScroll,
  } = useAutoScroll([localMessages, waitingForAI, streamingState]);
  const initialLoadRef = useRef(true);
  const knownMessageCountRef = useRef(0);
  const waitingSinceRef = useRef<number | null>(null);
  const dataAtWaitStartRef = useRef<number | null>(null);
  const prevDataUpdatedAtRef = useRef<number>(0);

  useEffect(() => {
    if (waitingForAI) {
      waitingSinceRef.current = Date.now();
    } else {
      waitingSinceRef.current = null;
      dataAtWaitStartRef.current = null;
      setPendingMessageId(null);
    }
  }, [waitingForAI]);

  // Clean up highlight ping timer on unmount
  useEffect(() => {
    return () => {
      if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
    };
  }, []);

  // Keep lastPendingIdRef in sync so we can merge agentSteps even after
  // pendingMessageId is cleared to null.
  useEffect(() => {
    if (pendingMessageId) {
      lastPendingIdRef.current = pendingMessageId;
    }
  }, [pendingMessageId]);

  // Sync server messages with local state and detect completed assistant
  // responses.  We use `dataUpdatedAt` (changes on every successful refetch)
  // instead of `chatData?.messages` alone — TanStack Query's structural
  // sharing can return the same array reference when the deep content hasn't
  // changed from its perspective, which would prevent the effect from firing.
  useEffect(() => {
    if (!chatData?.messages) return;

    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      prevDataUpdatedAtRef.current = dataUpdatedAt;
      const messages = chatData.messages;
      knownMessageCountRef.current = messages.length;
      setLocalMessages(messages);
      const last = messages[messages.length - 1];
      if (
        messages.length > 0 &&
        (last.role === "user" ||
          (last.role === "assistant" &&
            (last.status === "pending" || !last.content)))
      ) {
        setWaitingForAI(true);
      }
      return;
    }

    const dataChanged = dataUpdatedAt !== prevDataUpdatedAtRef.current;
    if (dataChanged) {
      prevDataUpdatedAtRef.current = dataUpdatedAt;
      setLocalMessages((prev) => {
        const mergeId = pendingMessageId ?? lastPendingIdRef.current;
        if (!mergeId) return chatData.messages;
        const localPending = prev.find((m) => m._id === mergeId);
        if (!localPending) return chatData.messages;
        return chatData.messages.map((m) =>
          m._id === mergeId ? mergeMessageStatus(localPending, m) : m,
        );
      });
      knownMessageCountRef.current = chatData.messages.length;
    }

    if (!waitingForAI) return;

    if (
      waitingSinceRef.current &&
      Date.now() - waitingSinceRef.current > POLL_TIMEOUT_MS
    ) {
      setWaitingForAI(false);
      return;
    }

    if (!dataChanged) return;

    // Skip response detection until we get fresh data after waiting started.
    // Without this, the effect fires with stale chatData (still showing the
    // previous assistant response) and immediately stops polling.
    if (
      dataAtWaitStartRef.current !== null &&
      dataUpdatedAt <= dataAtWaitStartRef.current
    ) {
      return;
    }

    const lastMsg = chatData.messages[chatData.messages.length - 1];
    if (
      lastMsg?.role === "assistant" &&
      lastMsg.content &&
      lastMsg.status !== "pending"
    ) {
      setWaitingForAI(false);
      dataAtWaitStartRef.current = null;
      // Reset canvas/ticket dismissed state when new response arrives
      if (lastMsg.canvasContent) {
        setCanvasDismissed(false);
      }
      if (lastMsg.viewTicketId) {
        setTicketDismissed(false);
        setReviewPolling(true);
      }
    }
    // Detect pending assistant message ID for lightweight polling
    if (waitingForAI && dataChanged && !pendingMessageId) {
      const pendingMsg = chatData.messages.find(
        (m) => m.role === "assistant" && (m.status === "pending" || !m.content),
      );
      if (pendingMsg?._id) {
        setPendingMessageId(pendingMsg._id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt, waitingForAI]);

  // Detect the pending assistant message ID for lightweight streaming poll
  useEffect(() => {
    if (!waitingForAI || !chatData?.messages || pendingMessageId) return;
    const lastMsg = chatData.messages[chatData.messages.length - 1];
    if (
      lastMsg?.role === "assistant" &&
      (lastMsg.status === "pending" || !lastMsg.content)
    ) {
      setPendingMessageId(lastMsg._id);
    }
  }, [chatData?.messages, waitingForAI, pendingMessageId]);

  // Merge lightweight streaming state into localMessages for faster UI updates
  useEffect(() => {
    if (!streamingState || !pendingMessageId) return;

    // Instrumentation: time-to-first-response
    if (
      !ttfrFiredRef.current &&
      messageSentAtRef.current &&
      (streamingState.streamingContent || streamingState.content)
    ) {
      const ttfrMs = Date.now() - messageSentAtRef.current;
      analytics.capture("ask_puntt_time_to_first_response", {
        chat_id: chatId,
        ttfr_ms: ttfrMs,
        workflow_type: chatData?.chat?.workflowType,
      });
      ttfrFiredRef.current = true;
    }

    setLocalMessages((prev) =>
      prev.map((msg) => {
        if (msg._id !== pendingMessageId) return msg;
        // Only write fields the streaming payload actually provides.
        // Unconditionally overwriting `canvasContent`/`canvasLoading` with
        // `undefined` was causing the right-pane canvas to unmount and
        // re-mount on every poll tick (visible as a full-pane flicker while
        // the user types follow-up instructions).
        const patch: Partial<AskPunttMessageExt> = {};
        if (streamingState.status !== undefined)
          patch.status = streamingState.status as AskPunttMessage["status"];
        if (streamingState.streamingContent !== undefined)
          patch.streamingContent = streamingState.streamingContent;
        if (streamingState.canvasLoading !== undefined)
          patch.canvasLoading = streamingState.canvasLoading;
        if (streamingState.suggestedResponses !== undefined)
          patch.suggestedResponses = streamingState.suggestedResponses;
        if (streamingState.content) patch.content = streamingState.content;
        if (streamingState.agentSteps?.length)
          patch.agentSteps =
            streamingState.agentSteps as AskPunttMessage["agentSteps"];
        if (streamingState.searchProgress?.length)
          patch.searchProgress =
            streamingState.searchProgress as AskPunttMessage["searchProgress"];
        if (streamingState.canvasContent)
          patch.canvasContent =
            streamingState.canvasContent as AskPunttMessage["canvasContent"];
        if (streamingState.canvasChangedSectionIds?.length)
          patch.canvasChangedSectionIds =
            streamingState.canvasChangedSectionIds;
        if (streamingState.viewTicketId)
          patch.viewTicketId = streamingState.viewTicketId;
        if (streamingState.projectData)
          patch.projectData =
            streamingState.projectData as AskPunttMessage["projectData"];
        if (streamingState.projectLoading !== undefined)
          patch.projectLoading = streamingState.projectLoading;
        if (streamingState.contentBlocks)
          patch.contentBlocks =
            streamingState.contentBlocks as AskPunttMessage["contentBlocks"];
        return { ...msg, ...patch };
      }),
    );
  }, [streamingState, pendingMessageId]);

  // Keep workflowType ref in sync so the unmount cleanup reads fresh data
  useEffect(() => {
    workflowTypeRef.current = chatData?.chat?.workflowType;
  }, [chatData?.chat?.workflowType]);

  useEffect(() => {
    analytics.capture("ask_puntt_chat_viewed", { chat_id: chatId });

    // Instrumentation: session depth on unmount
    return () => {
      const durationMs = Date.now() - sessionStartRef.current;
      analytics.capture("ask_puntt_session_ended", {
        chat_id: chatId,
        duration_ms: durationMs,
        message_count: messageCountRef.current,
        workflow_type: workflowTypeRef.current,
      });
    };
  }, [chatId]);

  // Detect the latest canvas content from messages
  const activeCanvas = useMemo((): {
    canvas: CanvasContent & { changedSectionIds?: string[] };
    skill?: string;
  } | null => {
    if (canvasDismissed) return null;
    // Find the last assistant message with canvasContent
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (
        msg.role === "assistant" &&
        msg.canvasContent &&
        (msg.canvasContent as CanvasContent).blocks?.length > 0
      ) {
        return {
          canvas: {
            ...(msg.canvasContent as CanvasContent),
            changedSectionIds: msg.canvasChangedSectionIds,
          },
          skill: msg.activeSkill ?? undefined,
        };
      }
    }
    return null;
  }, [localMessages, canvasDismissed]);

  // Instrumentation: canvas rendered (tracked per messageId to avoid duplicate events)
  const canvasTrackedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!activeCanvas) return;
    // Find the message ID for the current active canvas
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (
        msg.role === "assistant" &&
        msg.canvasContent &&
        (msg.canvasContent as CanvasContent).blocks?.length > 0
      ) {
        if (!canvasTrackedRef.current.has(msg._id)) {
          analytics.capture("ask_puntt_canvas_rendered", {
            chat_id: chatId,
            output_type: activeCanvas.canvas.outputType,
            block_count: activeCanvas.canvas.blocks?.length ?? 0,
            skill: activeCanvas.skill,
          });
          canvasTrackedRef.current.add(msg._id);
        }
        break;
      }
    }
  }, [activeCanvas, chatId, localMessages]);

  // Check if any message has canvas content (regardless of dismissed state)
  const hasCanvasContent = useMemo(() => {
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (
        msg.role === "assistant" &&
        msg.canvasContent &&
        (msg.canvasContent as CanvasContent).blocks?.length > 0
      ) {
        return true;
      }
    }
    return false;
  }, [localMessages]);

  // Detect canvas loading state (agent is generating canvas content)
  const isCanvasLoading = useMemo(() => {
    if (!waitingForAI) return false;
    const lastMsg = localMessages[localMessages.length - 1];
    return !!(lastMsg?.role === "assistant" && lastMsg.canvasLoading);
  }, [localMessages, waitingForAI]);

  // Detect project loading state (agent is generating project data)
  const isProjectLoading = useMemo(() => {
    if (!waitingForAI) return false;
    const lastMsg = localMessages[localMessages.length - 1];
    return !!(lastMsg?.role === "assistant" && lastMsg.projectLoading);
  }, [localMessages, waitingForAI]);

  // Detect the latest viewTicketId from messages
  const hasViewTicketId = useMemo(
    () => localMessages.some((m) => m.role === "assistant" && !!m.viewTicketId),
    [localMessages],
  );

  const activeViewTicketId = useMemo((): string | null => {
    if (ticketDismissed) return null;
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role === "assistant" && msg.viewTicketId) return msg.viewTicketId;
    }
    return null;
  }, [localMessages, ticketDismissed]);

  // LLM-provided pane labels for 3-panel layout (e.g. "Brief" / "Deliverable")
  const activePaneLabels = useMemo(() => {
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role === "assistant" && msg.paneLabels) return msg.paneLabels;
    }
    return undefined;
  }, [localMessages]);

  // Detect project data from chat-level or latest message
  const activeProjectData = useMemo((): AskPunttProjectData | null => {
    const chatLevel = chatData?.chat?.projectData as
      | AskPunttProjectData
      | null
      | undefined;
    if (chatLevel) return chatLevel;
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role === "assistant" && msg.projectData) {
        return msg.projectData as AskPunttProjectData;
      }
    }
    return null;
  }, [chatData?.chat?.projectData, localMessages]);

  // Track which ticket the user drilled into from the project view
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  // When the user drills into a project ticket, use that ID for data fetching.
  // For project chats, only show a ticket when explicitly selected (drill-in);
  // otherwise the project overview is the default landing view.
  const effectiveViewTicketId = activeProjectData
    ? selectedTicketId
    : activeViewTicketId;

  // Lift ticket data to parent to determine layout mode
  const {
    comments: viewComments,
    isLoading: viewTicketLoading,
    hasContextAssets,
    contextFiles,
    latestReviewFiles,
  } = useTicketViewData(effectiveViewTicketId, {
    pollingEnabled: reviewPolling,
  });

  // Auto-select the first comment when comments arrive and none is selected
  useEffect(() => {
    if (!selectedCommentId && viewComments?.length) {
      setSelectedCommentId(viewComments[0]._id ?? null);
    }
  }, [selectedCommentId, viewComments]);

  const isProjectChat = chatData?.chat?.workflowType === "project";

  // Enable 3-pane (brief + artwork + comments) when viewing a ticket with
  // context assets. In project chats this fires only when the user has
  // drilled into a specific ticket (effectiveViewTicketId falls back to
  // selectedTicketId when activeProjectData is set, see above), so the
  // project overview remains the default landing view.
  const use3PaneLayout = !!effectiveViewTicketId && hasContextAssets;

  const has2PaneRight =
    !use3PaneLayout &&
    !!(
      effectiveViewTicketId ||
      activeCanvas ||
      isProjectLoading ||
      activeProjectData
    );

  // Reset selected ticket when navigating to a different chat
  useEffect(() => {
    setSelectedTicketId(null);
  }, [chatId]);

  // Reset selected comment when switching between project tickets
  useEffect(() => {
    setSelectedCommentId(null);
  }, [selectedTicketId]);

  const [labelSheetCollapsed, setLabelSheetCollapsed] = useState(false);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [artworkCollapsed, setArtworkCollapsed] = useState(false);

  // When entering 3-column layout, collapse the app sidebar (if open) to free horizontal space
  const prev3Pane = useRef(false);
  useEffect(() => {
    if (use3PaneLayout && !prev3Pane.current && sidebarOpen) {
      dispatch(setDrawerOpen({ app: false }));
    }
    prev3Pane.current = use3PaneLayout;
  }, [use3PaneLayout, sidebarOpen, dispatch]);

  const handleDismissCanvas = useCallback(() => {
    setCanvasDismissed(true);
  }, []);

  const handleSectionClick = useCallback((sectionId: string, title: string) => {
    setSectionContext({ id: sectionId, title });
  }, []);

  const handleProjectDataUpdate = useCallback(
    (updatedData: AskPunttProjectData) => {
      queryClient.setQueryData(
        askPunttKeys.messages(chatId),
        (old: typeof chatData) => {
          if (!old?.chat) return old;
          return {
            ...old,
            chat: { ...old.chat, projectData: updatedData },
          };
        },
      );
    },
    [chatId, queryClient],
  );

  const labelSheetRef = useRef<AssetViewerPaneHandle>(null);
  const artworkPaneRef = useRef<AssetViewerPaneHandle>(null);
  const questionFormRef = useRef<QuestionFormHandle>(null);
  const [_questionHasAnswer, setQuestionHasAnswer] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentQuestionHasAnswer, setCurrentQuestionHasAnswer] =
    useState(false);
  const questionUploadInputRef = useRef<HTMLInputElement>(null);
  const [contextHighlight, setContextHighlight] = useState<{
    x: number;
    y: number;
    page?: number;
  } | null>(null);
  const contextHighlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const handleCitationClick = useCallback(
    (
      pageNumber: number,
      position?: { x: number; y: number },
      sourceUrl?: string,
      originalURL?: string,
    ) => {
      // Switch context pane to the file the citation points to. Signed URLs
      // carry ephemeral query-string signatures, so match on pathname only,
      // with originalURL as a fallback for files that carry an unsigned key.
      const targetIdx = findContextFileIndex(
        contextFiles,
        sourceUrl,
        originalURL,
      );
      if (targetIdx >= 0) {
        // Pass pageNumber along so it lands on the newly-mounted viewer even
        // when citations have no rel_x/rel_y to drive the highlight effect.
        labelSheetRef.current?.navigateToFile(targetIdx, pageNumber);
      } else {
        labelSheetRef.current?.navigateToPage(pageNumber);
      }
      if (position) {
        if (contextHighlightTimerRef.current)
          clearTimeout(contextHighlightTimerRef.current);
        // Clear first to restart the animation when clicking the same source again
        setContextHighlight(null);
        requestAnimationFrame(() => {
          setContextHighlight({
            x: position.x,
            y: position.y,
            page: pageNumber,
          });
          contextHighlightTimerRef.current = setTimeout(
            () => setContextHighlight(null),
            3000,
          );
        });
      }
    },
    [contextFiles],
  );

  useEffect(() => {
    return () => {
      if (contextHighlightTimerRef.current) {
        clearTimeout(contextHighlightTimerRef.current);
      }
    };
  }, []);

  const handleCommentClick = useCallback(
    (comment: {
      _id?: string;
      rel_x?: number;
      rel_y?: number;
      page_num?: number;
    }) => {
      setSelectedCommentId(comment._id ?? null);
      if (comment.rel_x != null && comment.rel_y != null) {
        if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
        setHighlightPing({
          x: comment.rel_x,
          y: comment.rel_y,
          page: comment.page_num,
        });
        focusTimerRef.current = setTimeout(() => setHighlightPing(null), 3000);
      }
    },
    [],
  );

  const pinDragMutation = useMutation({ mutationFn: editComment });

  const handlePinDragEnd = useCallback(
    (comment: BaseTicketComment, newRelX: number, newRelY: number) => {
      if (!activeViewTicketId || !comment._id) return;

      // Optimistic update: immediately reflect new position in cache
      queryClient.setQueryData<BaseTicketComment[]>(
        ["ticket-view-comments", activeViewTicketId],
        (old) =>
          old?.map((c) =>
            c._id === comment._id
              ? { ...c, rel_x: newRelX, rel_y: newRelY }
              : c,
          ),
      );

      pinDragMutation.mutate(
        {
          ticketId: activeViewTicketId,
          messageId: comment._id,
          payload: {
            description: comment.description ?? "",
            rel_x: newRelX,
            rel_y: newRelY,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["ticket-view-comments", activeViewTicketId],
            });
          },
          onError: () => {
            // Revert on failure
            queryClient.invalidateQueries({
              queryKey: ["ticket-view-comments", activeViewTicketId],
            });
          },
        },
      );
    },
    [activeViewTicketId, pinDragMutation, queryClient],
  );

  const handleStop = async () => {
    const msgId = pendingMessageId;
    // Optimistic: re-enable input immediately
    setWaitingForAI(false);
    setPendingMessageId(null);

    if (!msgId) return;

    try {
      await abortMessage.mutateAsync(msgId);
    } catch {
      // Revert: restore polling so the message can complete naturally
      setWaitingForAI(true);
      setPendingMessageId(msgId);
    }
  };

  const handleSend = async (
    overrideMessage?: string,
    extraAttachments?: {
      key: string;
      source: string;
      type: "image" | "video" | "file";
      name?: string;
    }[],
  ) => {
    const text = overrideMessage ?? inputMessage;
    const allAttachments = [...readyAttachments, ...(extraAttachments ?? [])];
    if (
      (!text.trim() && allAttachments.length === 0) ||
      sendMessage.isPending ||
      hasPending
    )
      return;
    resetAutoScroll();

    const messageToSend = text;
    const attachments = allAttachments;
    const messageLength = messageToSend.length;
    const messageIndex = localMessages.filter((m) => m.role === "user").length;

    const userMsg: AskPunttMessage = {
      _id: `local-user-${Date.now()}`,
      chatId,
      role: "user",
      content: messageToSend,
      attachments: attachments.length > 0 ? attachments : undefined,
      createdAt: new Date().toISOString(),
    };

    const contextToSend = sectionContext;

    setLocalMessages((prev) => [...prev, userMsg]);
    setInputMessage("");
    setSectionContext(null);
    clearAll();

    // Instrumentation: mark send time for TTFR measurement
    messageSentAtRef.current = Date.now();
    ttfrFiredRef.current = false;

    try {
      const result = await sendMessage.mutateAsync({
        message: messageToSend,
        attachments: attachments.length > 0 ? attachments : undefined,
        sectionContext: contextToSend ?? undefined,
      });

      messageCountRef.current += 1;

      // Replace the optimistic user message with the server-confirmed one
      setLocalMessages((prev) => {
        const withoutOptimistic = prev.filter((m) => m._id !== userMsg._id);
        return [...withoutOptimistic, result.userMessage];
      });
      knownMessageCountRef.current = localMessages.length;
      dataAtWaitStartRef.current = dataUpdatedAt;
      lastPendingIdRef.current = null;
      setWaitingForAI(true);

      const attachmentIds = attachments.map((att) => att.key);
      const chatConnectors = chatData?.chat?.enabledConnectors ?? [];

      analytics.capture("ask_puntt_message_sent", {
        chat_id: chatId,
        message_length: messageLength,
        has_attachments: attachments.length > 0,
        attachment_count: attachments.length,
        attachment_ids: attachmentIds,
        enabled_connectors: chatConnectors,
        message_index: messageIndex,
        workflow_id: result.workflowId,
      });
    } catch (error) {
      setLocalMessages((prev) => prev.filter((m) => m._id !== userMsg._id));

      const attachmentIds = attachments.map((att) => att.key);
      const chatConnectors = chatData?.chat?.enabledConnectors ?? [];

      analytics.capture("ask_puntt_message_send_error", {
        chat_id: chatId,
        has_attachments: attachments.length > 0,
        attachment_count: attachments.length,
        attachment_ids: attachmentIds,
        enabled_connectors: chatConnectors,
        ...errorAnalyticsPayload(error),
      });
      console.error("Failed to send message:", error);
    }
  };

  const chat = chatData?.chat;
  const serverReviewStatus = chat?.reviewStatus ?? null;

  // Show a transient "Review complete" when reviewStatus clears
  const [completionStatus, setCompletionStatus] = useState<string | null>(null);
  const prevReviewStatusRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevReviewStatusRef.current && !serverReviewStatus) {
      setCompletionStatus("Review complete");
      const timer = setTimeout(() => setCompletionStatus(null), 5000);
      return () => clearTimeout(timer);
    }
    if (serverReviewStatus) {
      setCompletionStatus(null);
    }
    prevReviewStatusRef.current = serverReviewStatus;
  }, [serverReviewStatus]);

  const reviewStatus = serverReviewStatus ?? completionStatus;

  // Sync review polling state with reviewStatus from server
  useEffect(() => {
    setReviewPolling(!!serverReviewStatus);
  }, [serverReviewStatus]);

  // When review polling stops, force a final fetch to pick up any comments
  // saved in the window between the last poll and polling stopping.
  const prevReviewPollingRef = useRef(false);
  useEffect(() => {
    if (prevReviewPollingRef.current && !reviewPolling && activeViewTicketId) {
      queryClient.invalidateQueries({
        queryKey: ["ticket-view", activeViewTicketId],
      });
      queryClient.invalidateQueries({
        queryKey: ["ticket-view-comments", activeViewTicketId],
      });
    }
    prevReviewPollingRef.current = reviewPolling;
  }, [reviewPolling, activeViewTicketId, queryClient]);

  // Find the active skill label from the last assistant message
  const activeSkillLabel = useMemo(() => {
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role === "assistant" && msg.activeSkill) {
        return SKILL_LABELS[msg.activeSkill];
      }
    }
    return undefined;
  }, [localMessages]);

  // Extract suggested responses from the last assistant message
  const activeSuggestedResponses = useMemo(() => {
    const lastMsg = localMessages[localMessages.length - 1];
    if (
      lastMsg?.role === "assistant" &&
      lastMsg.suggestedResponses &&
      lastMsg.suggestedResponses.length > 0
    ) {
      return lastMsg.suggestedResponses;
    }
    return null;
  }, [localMessages]);

  // Hide suggestion pills when the last message has userQuestions (the form handles it)
  const lastMessage = localMessages[localMessages.length - 1];
  const hasQuestionForm =
    lastMessage?.role === "assistant" &&
    lastMessage.userQuestions &&
    lastMessage.userQuestions.length > 0;

  // Reset answer state when a new question form appears
  useEffect(() => {
    if (hasQuestionForm) {
      setQuestionHasAnswer(false);
      setCurrentQuestionHasAnswer(false);
      setCurrentQuestionIndex(0);
    }
  }, [hasQuestionForm]);

  const totalQuestions = hasQuestionForm
    ? (lastMessage?.userQuestions?.length ?? 0)
    : 0;

  const handleQuestionNext = useCallback(() => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      setCurrentQuestionHasAnswer(false);
    } else {
      questionFormRef.current?.submit();
    }
  }, [currentQuestionIndex, totalQuestions]);

  const handleQuestionBack = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  }, [currentQuestionIndex]);

  const handleUploadRequest = useCallback(() => {
    questionUploadInputRef.current?.click();
  }, []);
  const showSuggestions =
    activeSuggestedResponses &&
    !waitingForAI &&
    !sendMessage.isPending &&
    !hasQuestionForm;

  return (
    <AskPunttDropZone
      onFilesDropped={addFiles}
      className="flex h-full flex-col overflow-hidden"
      data-testid="ask-puntt-chat"
    >
      {/* Breadcrumb */}
      <PageHeader>
        <Link to={askPunttRoute.to} className="puntt-link">
          <Text
            size="2"
            weight="medium"
            className="text-puntt-accent-11 hover:underline"
          >
            Ask Puntt
          </Text>
        </Link>
        {chat?.title && (
          <>
            <CaretRight size={12} className="text-puntt-neutral-gray-9" />
            <Text
              size="2"
              weight="medium"
              className="max-w-[400px] truncate text-puntt-neutral-gray-11"
            >
              {chat.title}
            </Text>
          </>
        )}
        {activeSkillLabel && (
          <Badge size="1" variant="soft" color="blue" className="ml-2">
            {activeSkillLabel}
          </Badge>
        )}
        {ticketDismissed && hasViewTicketId && (
          <Button
            size="1"
            variant="soft"
            color="blue"
            className="ml-auto cursor-pointer"
            onClick={() => setTicketDismissed(false)}
          >
            <ArrowsOutSimple size={14} />
            <Text size="1">Show Review</Text>
          </Button>
        )}
        {canvasDismissed && hasCanvasContent && (
          <Button
            size="1"
            variant="soft"
            color="blue"
            className={`cursor-pointer ${!(ticketDismissed && hasViewTicketId) ? "ml-auto" : ""}`}
            onClick={() => setCanvasDismissed(false)}
          >
            <ArrowsOutSimple size={14} />
            <Text size="1">Show Canvas</Text>
          </Button>
        )}
      </PageHeader>

      {/* Main content: multi-pane layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Chat Pane */}
        <div
          className={`relative flex flex-col overflow-hidden transition-[width,opacity] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            chatCollapsed
              ? "w-0 opacity-0"
              : has2PaneRight || use3PaneLayout
                ? artworkCollapsed && !use3PaneLayout
                  ? "w-full border-r border-puntt-neutral-gray-5"
                  : "min-w-0 flex-1 border-r border-puntt-neutral-gray-5"
                : "w-full"
          }`}
        >
          {(use3PaneLayout || has2PaneRight) && !chatCollapsed && (
            <div className="flex shrink-0 items-center justify-end border-b border-puntt-neutral-gray-5 px-2 py-1">
              <Tooltip content="Collapse chat pane">
                <IconButton
                  size="1"
                  variant="ghost"
                  color="gray"
                  aria-label="Collapse chat pane"
                  data-testid="pane-collapse-chat"
                  onClick={() => setChatCollapsed(true)}
                >
                  <ArrowsInSimple size={14} />
                </IconButton>
              </Tooltip>
            </div>
          )}
          {/* Messages */}
          <div
            ref={scrollContainerRef}
            onScroll={handleMessagesScroll}
            className="flex-1 overflow-y-auto px-4 py-6"
            data-testid="ask-puntt-messages"
          >
            <div className="mx-auto max-w-[720px] space-y-4">
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="h-16 animate-pulse rounded-lg bg-puntt-neutral-gray-4"
                    />
                  ))}
                </div>
              ) : (
                <>
                  {localMessages.map((message, idx) => {
                    const isLast = idx === localMessages.length - 1;
                    const isLastAssistant =
                      message.role === "assistant" && isLast && !waitingForAI;
                    const precedingUserHasAttachments =
                      isLast &&
                      message.role === "assistant" &&
                      waitingForAI &&
                      !!localMessages[idx - 1]?.attachments?.length;

                    // Inject the collapsed checklist as a chat step directly
                    // before the AI's final response so it reads as part of
                    // Append checklist AFTER the last AI message (bottom of
                    // agent output). Pass live reviewPolling so the widget
                    // remains in "in-progress" state while the Temporal review
                    // is still running after the agent has responded.
                    const showChecklistAfter =
                      !activeProjectData &&
                      !!chatData?.chat?.reviewChecklist &&
                      !waitingForAI &&
                      message.role === "assistant" &&
                      isLast;

                    return (
                      <Fragment key={message._id}>
                        <ChatMessage
                          message={message}
                          chatId={chatId}
                          reviewFiles={latestReviewFiles}
                          localPreviewUrls={localPreviewUrls}
                          isStreaming={
                            waitingForAI &&
                            isLast &&
                            message.role === "assistant"
                          }
                          isLastAssistant={isLastAssistant}
                          onSubmitAnswers={handleSend}
                          formDisabled={sendMessage.isPending || waitingForAI}
                          questionFormRef={
                            isLast && hasQuestionForm
                              ? questionFormRef
                              : undefined
                          }
                          onAnswerChange={
                            isLast && hasQuestionForm
                              ? setQuestionHasAnswer
                              : undefined
                          }
                          hideInlineQuestionForm={!!(isLast && hasQuestionForm)}
                          precedingUserHasAttachments={
                            precedingUserHasAttachments
                          }
                        />
                        {showChecklistAfter && (
                          <ReviewChecklist
                            workflowType={chatData?.chat?.workflowType ?? ""}
                            isReviewPolling={reviewPolling}
                            reviewComplete={
                              !reviewPolling &&
                              !!chatData?.chat?.reviewChecklist
                            }
                            dynamicItems={chatData?.chat?.reviewChecklist}
                          />
                        )}
                      </Fragment>
                    );
                  })}
                  {/* While waiting: show checklist as soon as create_ticket
                      fires in agent steps (before reviewChecklist is saved),
                      or fall back to the generic ThinkingState dot. */}
                  {waitingForAI &&
                    localMessages[localMessages.length - 1]?.role !==
                      "assistant" &&
                    (() => {
                      const hasCreateTicket = localMessages.some((m) =>
                        m.agentSteps?.some(
                          (s) =>
                            s.type === "tool_call" &&
                            s.tool === "create_ticket",
                        ),
                      );
                      const showChecklist =
                        !activeProjectData &&
                        (reviewPolling ||
                          !!chatData?.chat?.reviewChecklist ||
                          hasCreateTicket);
                      return showChecklist ? (
                        <ReviewChecklist
                          workflowType={chatData?.chat?.workflowType ?? ""}
                          isReviewPolling={reviewPolling}
                          reviewComplete={false}
                          dynamicItems={chatData?.chat?.reviewChecklist}
                        />
                      ) : (
                        <ThinkingState
                          precedingUserHasAttachments={
                            !!localMessages[localMessages.length - 1]
                              ?.attachments?.length
                          }
                        />
                      );
                    })()}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Area */}
          <div className="shrink-0 border-t border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2 p-4">
            <div className="mx-auto max-w-[720px] space-y-3">
              {activeProjectData && (
                <ProjectReviewStatusBanner projectData={activeProjectData} />
              )}
              <div
                {...dropZoneProps}
                className={`relative overflow-hidden rounded-2xl border bg-base-white shadow-sm transition-shadow duration-200 focus-within:border-puntt-accent-7 focus-within:shadow-md focus-within:ring-2 focus-within:ring-puntt-accent-6/20 ${isDragOver ? "border-puntt-accent-9 ring-2 ring-puntt-accent-6/40" : "border-puntt-neutral-gray-5"}`}
                data-testid="ask-puntt-chat-drop-zone"
              >
                {isDragOver && (
                  <div className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center rounded-2xl bg-puntt-accent-2/80 backdrop-blur-sm">
                    <Upload
                      size={28}
                      weight="duotone"
                      className="mb-1 text-puntt-accent-9"
                    />
                    <Text
                      size="2"
                      weight="medium"
                      className="text-puntt-accent-11"
                    >
                      Drop files here
                    </Text>
                  </div>
                )}

                <input
                  ref={questionUploadInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files) addFiles(Array.from(e.target.files));
                    e.target.value = "";
                  }}
                />

                {hasQuestionForm && !waitingForAI && (
                  <div className="border-b border-puntt-neutral-gray-4 px-4 pb-2 pt-3">
                    <Flex align="center" justify="between">
                      <Text size="1" className="text-puntt-neutral-gray-9">
                        Question {currentQuestionIndex + 1} of {totalQuestions}
                      </Text>
                      <Flex gap="1">
                        {Array.from({ length: totalQuestions }).map((_, i) => (
                          <div
                            key={i}
                            className={`size-1.5 rounded-full transition-colors duration-200 ${
                              i <= currentQuestionIndex
                                ? "bg-puntt-accent-9"
                                : "bg-puntt-neutral-gray-5"
                            }`}
                          />
                        ))}
                      </Flex>
                    </Flex>
                  </div>
                )}

                {showSuggestions && (
                  <SuggestedResponses
                    suggestions={activeSuggestedResponses}
                    onSelect={(suggestion) => {
                      analytics.capture(
                        "ask_puntt_suggested_response_clicked",
                        {
                          chat_id: chatId,
                          suggestion_text: suggestion.slice(0, 100),
                          workflow_type: chatData?.chat?.workflowType,
                        },
                      );
                      handleSend(suggestion);
                    }}
                    disabled={sendMessage.isPending || waitingForAI}
                  />
                )}

                {/* Section context chip */}
                {sectionContext && (
                  <div className="flex animate-chip-slide-down items-center gap-2 overflow-hidden border-b border-puntt-accent-4 bg-puntt-accent-2 px-3 py-1.5 text-xs text-puntt-accent-11">
                    <span className="flex-1 truncate">
                      Re: <strong>{sectionContext.title}</strong>
                    </span>
                    <button
                      onClick={() => setSectionContext(null)}
                      aria-label="Remove section context"
                      className="shrink-0 cursor-pointer rounded p-0.5 hover:bg-puntt-accent-3 focus:outline-none focus:ring-1 focus:ring-puntt-accent-9"
                    >
                      ×
                    </button>
                  </div>
                )}
                {hasQuestionForm && !waitingForAI ? (
                  <div className="px-3 py-2">
                    <QuestionForm
                      ref={questionFormRef}
                      questions={
                        (lastMessage?.userQuestions as AskPunttUserQuestion[]) ??
                        []
                      }
                      onSubmit={(formatted, attachments) => {
                        analytics.capture("ask_puntt_question_form_submitted", {
                          chat_id: chatId,
                          question_count: totalQuestions,
                          workflow_type: chatData?.chat?.workflowType,
                        });
                        handleSend(formatted || undefined, attachments);
                      }}
                      disabled={
                        sendMessage.isPending || waitingForAI || hasPending
                      }
                      hideSubmitButton
                      visibleQuestionIndex={currentQuestionIndex}
                      onCurrentQuestionAnswerChange={
                        setCurrentQuestionHasAnswer
                      }
                      onUploadRequest={handleUploadRequest}
                      chatLevelFileCount={attachedItems.length}
                    />
                  </div>
                ) : (
                  <TextArea
                    data-testid="ask-puntt-chat-reply-input"
                    size="2"
                    variant="surface"
                    placeholder="Reply..."
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    onPaste={handlePaste}
                    className="min-h-[60px] w-full resize-none border-none !shadow-none"
                    style={{ outline: "none", boxShadow: "none" }}
                  />
                )}

                {/* Attached files preview */}
                {attachedItems.length > 0 && (
                  <Flex gap="2" wrap="wrap" className="px-3 pb-1">
                    {attachedItems.map((item, idx) => (
                      <Flex
                        key={item.id}
                        align="center"
                        gap="1"
                        className="rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                        data-testid="ask-puntt-attached-file"
                      >
                        {item.status === "pending" && (
                          <CircleNotch
                            size={12}
                            weight="bold"
                            className="shrink-0 animate-spin text-puntt-neutral-gray-9"
                          />
                        )}
                        <Text
                          size="1"
                          className="max-w-[150px] truncate text-puntt-neutral-gray-11"
                        >
                          {item.file.name}
                          {item.status === "error" && " (failed)"}
                        </Text>
                        <button
                          onClick={() => removeFile(idx)}
                          className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
                          aria-label={`Remove ${item.file.name}`}
                        >
                          ×
                        </button>
                      </Flex>
                    ))}
                  </Flex>
                )}

                <Flex justify="between" align="center" className="px-3 py-1.5">
                  {hasQuestionForm && !waitingForAI ? (
                    <>
                      <Button
                        size="2"
                        variant="ghost"
                        color="gray"
                        disabled={sendMessage.isPending || waitingForAI}
                        onClick={() => questionFormRef.current?.submitAny()}
                        className="cursor-pointer"
                      >
                        Just Get Started
                        <ArrowRight size={14} weight="bold" />
                      </Button>
                      <Flex gap="2" align="center">
                        <Button
                          size="2"
                          variant="soft"
                          color="gray"
                          disabled={
                            currentQuestionIndex === 0 ||
                            sendMessage.isPending ||
                            waitingForAI
                          }
                          onClick={handleQuestionBack}
                          className="cursor-pointer"
                        >
                          <ArrowLeft size={14} weight="bold" />
                          Back
                        </Button>
                        <Button
                          data-testid="question-next-btn"
                          size="2"
                          variant="solid"
                          color="blue"
                          disabled={
                            !currentQuestionHasAnswer ||
                            sendMessage.isPending ||
                            waitingForAI ||
                            hasPending
                          }
                          onClick={handleQuestionNext}
                          className="cursor-pointer"
                        >
                          {currentQuestionIndex < totalQuestions - 1
                            ? "Next"
                            : "Submit Answers"}
                          <ArrowRight size={14} weight="bold" />
                        </Button>
                      </Flex>
                    </>
                  ) : (
                    <>
                      <InputPlusMenu
                        onFilesSelected={addFiles}
                        onScreenshotCaptured={addFile}
                        enabledConnectors={connectorsArrayToRecord(
                          chat?.enabledConnectors,
                          availableConnectors,
                        )}
                        connectorsDisabled
                        availableConnectors={availableConnectors}
                      />
                      {waitingForAI ? (
                        <Button
                          data-testid="ask-puntt-chat-stop"
                          size="2"
                          variant="soft"
                          color="gray"
                          onClick={handleStop}
                          disabled={abortMessage.isPending}
                          className="cursor-pointer transition-all duration-200"
                          aria-label="Stop generating"
                        >
                          <Stop size={14} weight="fill" />
                          Stop
                        </Button>
                      ) : (
                        <Button
                          data-testid="ask-puntt-chat-send"
                          size="2"
                          variant={
                            inputMessage.trim() || readyAttachments.length > 0
                              ? "solid"
                              : "ghost"
                          }
                          color={
                            hasPending
                              ? "gray"
                              : inputMessage.trim() ||
                                  readyAttachments.length > 0
                                ? "blue"
                                : "gray"
                          }
                          disabled={
                            (!inputMessage.trim() &&
                              readyAttachments.length === 0) ||
                            sendMessage.isPending ||
                            hasPending
                          }
                          onClick={() => handleSend()}
                          className="cursor-pointer transition-all duration-200"
                          aria-label="Send message"
                        >
                          {hasPending ? (
                            <CircleNotch
                              size={14}
                              weight="bold"
                              className="animate-spin"
                            />
                          ) : (
                            <ArrowUp size={14} weight="bold" />
                          )}
                        </Button>
                      )}
                    </>
                  )}
                </Flex>
              </div>
            </div>
          </div>
        </div>

        {/* Right Pane(s) */}
        {use3PaneLayout ? (
          <>
            {/* Collapsed Chat label */}
            {chatCollapsed && (
              <button
                onClick={() => setChatCollapsed(false)}
                className="flex h-full w-8 shrink-0 cursor-pointer items-center justify-center border-r border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2 transition-colors hover:bg-puntt-neutral-gray-3"
                aria-label="Expand Chat pane"
                data-testid="pane-expand-chat"
              >
                <Text
                  size="1"
                  weight="medium"
                  className="whitespace-nowrap uppercase tracking-wide text-puntt-neutral-gray-9"
                  style={{
                    writingMode: "vertical-rl",
                    transform: "rotate(180deg)",
                  }}
                >
                  Chat
                </Text>
              </button>
            )}

            {/* Collapsed Context label */}
            {labelSheetCollapsed && (
              <button
                onClick={() => setLabelSheetCollapsed(false)}
                className="flex h-full w-8 shrink-0 cursor-pointer items-center justify-center border-r border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2 transition-colors hover:bg-puntt-neutral-gray-3"
                aria-label="Expand Context pane"
                data-testid="pane-expand-context"
              >
                <Text
                  size="1"
                  weight="medium"
                  className="whitespace-nowrap uppercase tracking-wide text-puntt-neutral-gray-9"
                  style={{
                    writingMode: "vertical-rl",
                    transform: "rotate(180deg)",
                  }}
                >
                  {activePaneLabels?.context ?? "Context"} &middot;{" "}
                  {contextFiles.length}{" "}
                  {contextFiles.length === 1 ? "file" : "files"}
                </Text>
              </button>
            )}

            {/* Context pane */}
            <div
              data-testid="pane-context"
              className={`animate-canvas-slide-in transition-[width] duration-300 ease-in-out ${
                labelSheetCollapsed
                  ? "w-0 overflow-hidden"
                  : "min-w-0 flex-1 border-r border-puntt-neutral-gray-5"
              }`}
            >
              <div
                className={`h-full transition-[opacity,transform] duration-200 ease-out ${
                  labelSheetCollapsed
                    ? "translate-x-5 opacity-0"
                    : "translate-x-0 opacity-100"
                }`}
              >
                {viewTicketLoading ? (
                  <div className="flex h-full items-center justify-center bg-base-white">
                    <Text size="2" className="text-puntt-neutral-gray-9">
                      Loading...
                    </Text>
                  </div>
                ) : (
                  <AssetViewerPane
                    ref={labelSheetRef}
                    title={activePaneLabels?.context ?? "Context"}
                    assets={contextFiles}
                    highlightPosition={contextHighlight}
                    onCollapse={() => setLabelSheetCollapsed(true)}
                  />
                )}
              </div>
            </div>

            {/* Collapsed Artwork label */}
            {artworkCollapsed && (
              <button
                onClick={() => setArtworkCollapsed(false)}
                className="flex h-full w-8 shrink-0 cursor-pointer items-center justify-center border-l border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2 transition-colors hover:bg-puntt-neutral-gray-3"
                aria-label="Expand Artwork pane"
                data-testid="pane-expand-artwork"
              >
                <Text
                  size="1"
                  weight="medium"
                  className="whitespace-nowrap uppercase tracking-wide text-puntt-neutral-gray-9"
                  style={{
                    writingMode: "vertical-rl",
                    transform: "rotate(180deg)",
                  }}
                >
                  {activePaneLabels?.artwork ?? "Artwork"} &middot;{" "}
                  {latestReviewFiles.length}{" "}
                  {latestReviewFiles.length === 1 ? "file" : "files"}
                </Text>
              </button>
            )}

            {/* Artwork pane */}
            <div
              data-testid="pane-artwork"
              className={`animate-canvas-slide-in transition-[width] duration-300 ease-in-out ${
                artworkCollapsed ? "w-0 overflow-hidden" : "min-w-0 flex-1"
              }`}
            >
              <div
                className={`h-full transition-[opacity,transform] duration-200 ease-out ${
                  artworkCollapsed
                    ? "translate-x-5 opacity-0"
                    : "translate-x-0 opacity-100"
                }`}
              >
                {viewTicketLoading ? (
                  <div className="flex h-full items-center justify-center bg-base-white">
                    <Text size="2" className="text-puntt-neutral-gray-9">
                      Loading...
                    </Text>
                  </div>
                ) : (
                  <AssetViewerPane
                    ref={artworkPaneRef}
                    title={activePaneLabels?.artwork ?? "Artwork"}
                    assets={latestReviewFiles}
                    reviewStatus={reviewStatus}
                    comments={viewComments ?? []}
                    activeCommentId={selectedCommentId}
                    onCommentClick={handleCommentClick}
                    onPinClick={handleCommentClick}
                    onPinDragEnd={handlePinDragEnd}
                    onCitationClick={handleCitationClick}
                    onCollapse={() => setArtworkCollapsed(true)}
                    highlightPosition={highlightPing}
                    ticketId={effectiveViewTicketId ?? undefined}
                    onBackToProject={
                      selectedTicketId && activeProjectData
                        ? () => setSelectedTicketId(null)
                        : undefined
                    }
                  />
                )}
              </div>
            </div>
          </>
        ) : has2PaneRight ? (
          <>
            {/* Collapsed Chat expand bar (2-pane) */}
            {chatCollapsed && (
              <button
                onClick={() => setChatCollapsed(false)}
                className="flex h-full w-8 shrink-0 cursor-pointer items-center justify-center border-r border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2 transition-colors hover:bg-puntt-neutral-gray-3"
                aria-label="Expand Chat pane"
                data-testid="pane-expand-chat"
              >
                <Text
                  size="1"
                  weight="medium"
                  className="whitespace-nowrap uppercase tracking-wide text-puntt-neutral-gray-9"
                  style={{
                    writingMode: "vertical-rl",
                    transform: "rotate(180deg)",
                  }}
                >
                  Chat
                </Text>
              </button>
            )}

            {/* Collapsed right-pane expand bar (2-pane) */}
            {artworkCollapsed && (
              <button
                onClick={() => setArtworkCollapsed(false)}
                className="flex h-full w-8 shrink-0 cursor-pointer items-center justify-center border-l border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2 transition-colors hover:bg-puntt-neutral-gray-3"
                aria-label="Expand pane"
                data-testid="pane-expand-artwork"
              >
                <Text
                  size="1"
                  weight="medium"
                  className="whitespace-nowrap uppercase tracking-wide text-puntt-neutral-gray-9"
                  style={{
                    writingMode: "vertical-rl",
                    transform: "rotate(180deg)",
                  }}
                >
                  {effectiveViewTicketId
                    ? "Ticket"
                    : activeCanvas
                      ? "Canvas"
                      : "Details"}
                </Text>
              </button>
            )}

            {/* Right pane content (2-pane) */}
            <div
              className={`animate-canvas-slide-in transition-[width] duration-300 ease-in-out ${
                artworkCollapsed
                  ? "w-0 overflow-hidden"
                  : chatCollapsed
                    ? "w-full"
                    : "min-w-0 flex-1"
              }`}
            >
              <div
                className={`h-full transition-[opacity,transform] duration-200 ease-out ${
                  artworkCollapsed
                    ? "translate-x-5 opacity-0"
                    : "translate-x-0 opacity-100"
                }`}
              >
                {activeProjectData && selectedTicketId ? (
                  <TicketViewPane
                    ticketId={selectedTicketId}
                    onCollapse={() => setArtworkCollapsed(true)}
                    onBackToProject={() => setSelectedTicketId(null)}
                    reviewStatus={reviewStatus}
                    onCitationClick={handleCitationClick}
                    onPinDragEnd={handlePinDragEnd}
                  />
                ) : isProjectChat && isProjectLoading && !activeProjectData ? (
                  <ProjectLoadingPane />
                ) : activeCanvas ? (
                  <div
                    className="relative h-full"
                    data-testid="ask-puntt-canvas-area"
                  >
                    <CanvasPane
                      canvas={activeCanvas.canvas}
                      onDismiss={handleDismissCanvas}
                      skillName={
                        activeCanvas.skill
                          ? SKILL_LABELS[activeCanvas.skill]
                          : undefined
                      }
                      chatId={chatId}
                      isLoading={isCanvasLoading}
                      onSectionClick={handleSectionClick}
                    />
                  </div>
                ) : activeProjectData ? (
                  <ProjectStatusPane
                    projectData={activeProjectData}
                    activeTicketId={effectiveViewTicketId}
                    ticketIds={chatData?.chat?.ticketIds ?? []}
                    onTicketSelect={setSelectedTicketId}
                    chatId={chatId}
                    chatCreatedAt={chatData?.chat?.createdAt}
                    onSendMessage={(msg) => handleSend(msg)}
                    onProjectDataUpdate={handleProjectDataUpdate}
                    actionsDisabled={sendMessage.isPending || waitingForAI}
                    isLoading={isProjectLoading}
                  />
                ) : effectiveViewTicketId ? (
                  <TicketViewPane
                    ticketId={effectiveViewTicketId}
                    onCollapse={() => setArtworkCollapsed(true)}
                    reviewStatus={reviewStatus}
                    onCitationClick={handleCitationClick}
                    onPinDragEnd={handlePinDragEnd}
                  />
                ) : null}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </AskPunttDropZone>
  );
}
